import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ReactComponent as MatchYourBrandPerfectlyIcon } from "../../images/svgPaths/MatchYourBrandPerfectlyIcon.svg";
import { ReactComponent as CustomisableCheckoutLanguageIcon } from "../../images/svgPaths/CustomisableCheckoutLanguageIcon.svg";
import { ReactComponent as WalletIcon } from "../../images/svgPaths/WalletIcon.svg";
import { ReactComponent as TakeFullControlIcon } from "../../images/svgPaths/TakeFullControlIcon.svg";

function CustomizableCheckoutSection() {
  const customizableCheckoutSection = useRef(null);
  const customizableCheckoutSectionBackground = useRef(null);

  const customizableCheckoutSectionCardOne = useRef(null);
  const customizableCheckoutSectionCardTwo = useRef(null);
  const customizableCheckoutSectionCardThree = useRef(null);
  const customizableCheckoutSectionCardFour = useRef(null);

  useGSAP(
    () => {
      gsap.fromTo(
        customizableCheckoutSectionBackground.current,
        {
          height: "0%",
        },
        {
          scrollTrigger: {
            trigger: customizableCheckoutSection.current,
            start: "top 75%",
            end: "top 60%",
            scrub: false,
            toggleActions: "play none none reverse",
          },
          height: "100%",
          duration: 1.5,
          ease: "sine.out",
        }
      );

      gsap.to(customizableCheckoutSectionCardOne.current, {
        scrollTrigger: {
          trigger: customizableCheckoutSection.current,
          start: "top 65%",
          end: "top 10%",
          scrub: true,
        },
        x: 0,
        y: 0,
        duration: 1.5,
      });

      gsap.to(customizableCheckoutSectionCardTwo.current, {
        scrollTrigger: {
          trigger: customizableCheckoutSection.current,
          start: "top 65%",
          end: "top 10%",
          scrub: true,
        },
        x: 0,
        y: 0,
        duration: 1.5,
      });

      gsap.to(customizableCheckoutSectionCardThree.current, {
        scrollTrigger: {
          trigger: customizableCheckoutSection.current,
          start: "top 65%",
          end: "top 10%",
          scrub: true,
        },
        x: 0,
        y: 0,
        duration: 1.5,
      });

      gsap.to(customizableCheckoutSectionCardFour.current, {
        scrollTrigger: {
          trigger: customizableCheckoutSection.current,
          start: "top 65%",
          end: "top 10%",
          scrub: true,
        },
        x: 0,
        y: 0,
        duration: 1.5,
      });
    },
    {
      scope: customizableCheckoutSection.current,
    }
  );

  const cardStyles = {
    section:
      "min-w-[20rem] sm:min-w-0 max-w-xs bg-white border border-black rounded-[16px] xl:rounded-[20px] py-5 px-4 shadow-[6px_6px_0_black]",
    iconSection:
      "border-2 border-black rounded-[10px] xl:rounded-[20px] w-full h-[129px] xl:h-[200px] flex justify-center items-center relative",
    textSection: {
      section: "mt-5 xl:mt-9 pb-0 xl:pb-4 flex flex-col gap-y-1.5 xl:gap-y-2.5",
      heading:
        "text-base xl:text-lg font-HalvarBreitBold text-[#100C08] leading-[18.53px] xl:leading-[27px] text-center",
      paragraph:
        "text-sm xl:text-base font-HalvarBreitRegular text-[#100C08] leading-[21px] xl:leading-[24px] text-center",
    },
  };

  return (
    <section
      className="z-[3] w-full mt-[9.25rem] xl:mt-[7rem] bg-white py-[100px] px-4 flex flex-col items-center relative overflow-hidden min-h-[900px]"
      ref={customizableCheckoutSection}
    >
      <div
        ref={customizableCheckoutSectionBackground}
        className="absolute top-0 left-0 w-full h-full bg-[#2D2D2D] z-[-1] transition-all ease-out"
      ></div>

      <h2 className="text-[42px] xl:text-[52px] font-HalvarBreitBold text-white text-center leading-[48.64px] xl:leading-[34px] -tracking-[2%] xl:-tracking-[1.04px] capitalize">
        Customisable Checkout
      </h2>
      <p className="mt-[22px] text-base sm:text-lg xl:text-[22px] font-HalvarBreitMedium text-white text-center leading-[26.56px] xl:leading-[36.52px] max-w-2xl">
        Take control of your entire checkout process
        <br className="hidden sm:block xl:hidden" />
        from just 1 simple dashboard
      </p>
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8 sm:gap-4 xl:gap-8 mt-16">
        {/* 1 */}
        <section
          ref={customizableCheckoutSectionCardOne}
          className={`${cardStyles.section} translate-x-[160%] z-[2]`}
        >
          <section className="px-3">
            <section className={cardStyles.iconSection}>
              <MatchYourBrandPerfectlyIcon className="w-[77px] xl:w-[93px]" />
            </section>
          </section>
          <section className={cardStyles.textSection.section}>
            <h3 className={cardStyles.textSection.heading}>
              Match your brand
              <br className="hidden xl:block" />
              perfectly
            </h3>
            <p className={cardStyles.textSection.paragraph}>
              Upload your logo and select colors that match your brand's
              aesthetic for a tailored customer experience.
            </p>
          </section>
        </section>

        {/* 2 */}
        <section
          ref={customizableCheckoutSectionCardTwo}
          className={`${cardStyles.section} translate-x-[50%]`}
        >
          <section className="px-3">
            <section className={cardStyles.iconSection}>
              <CustomisableCheckoutLanguageIcon className="w-[77px] xl:w-[107px]" />
            </section>
          </section>
          <section className={cardStyles.textSection.section}>
            <h3 className={cardStyles.textSection.heading}>
              Go global, go local!
            </h3>
            <p className={cardStyles.textSection.paragraph}>
              Personalizes for regional preferences, ensuring a seamless
              experience for all.
            </p>
          </section>
        </section>

        {/* 3 */}
        <section
          ref={customizableCheckoutSectionCardThree}
          className={`${cardStyles.section} -translate-x-[60%]`}
        >
          <section className="px-3">
            <section className={cardStyles.iconSection}>
              <WalletIcon className="w-[66.72px] xl:w-[91px]" />
            </section>
          </section>
          <section className={cardStyles.textSection.section}>
            <h3 className={cardStyles.textSection.heading}>
              Give your customers the flexibility to pay their way
            </h3>
            <p className={cardStyles.textSection.paragraph}>
              Offer modern payment options like Apple Pay, Google Pay & Wallets
              as well
            </p>
          </section>
        </section>

        {/* 4 */}
        <section
          ref={customizableCheckoutSectionCardFour}
          className={`${cardStyles.section} -translate-x-[170%]`}
        >
          <section className="px-3">
            <section className={cardStyles.iconSection}>
              <TakeFullControlIcon className="w-[71.56px] xl:w-[88.09px]" />
            </section>
          </section>
          <section className={cardStyles.textSection.section}>
            <h3 className={cardStyles.textSection.heading}>
              Take full control
            </h3>
            <p className={cardStyles.textSection.paragraph}>
              STRABL Checkout allows you to tailor the experience to your
              specific needs.
              <br className="block xl:hidden" />
              Create a checkout experience that works for your business
            </p>
          </section>
        </section>
      </section>
    </section>
  );
}

export default CustomizableCheckoutSection;
